import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { formatMoney } from "../lib/money";
import { CheckCircle2, AlertCircle, Clock, Download, ArrowRight, Sparkles } from "lucide-react";

/**
 * Sprint 4 — Unified payment result page.
 * Routes /dashboard/billing/success, /failed, /cancel, /pending all render
 * here based on the route prop OR the polled status of the transaction.
 */
export default function PaymentResultPage({ defaultStatus = "pending" }) {
  const [search] = useSearchParams();
  const ptId = search.get("payment_transaction_id");
  const sessionId = search.get("session_id");
  const orderRef = ptId || sessionId;
  const [pt, setPt] = useState(null);
  const [status, setStatus] = useState(defaultStatus);

  useEffect(() => {
    if (!orderRef) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${orderRef}`);
        if (cancelled) return;
        setPt(data);
        setStatus(data.status);
        if (data.status === "paid" || data.status === "failed") return;
        setTimeout(poll, 2500);
      } catch { /* noop */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderRef]);

  const isPaid = status === "paid";
  const isFailed = status === "failed";
  const Icon = isPaid ? CheckCircle2 : isFailed ? AlertCircle : Clock;
  const tone = isPaid ? "emerald" : isFailed ? "rose" : "amber";

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="payment-result-root">
        <div className={`rounded-2xl border bg-white p-8 text-center
                         ${tone === "emerald" ? "border-emerald-300" :
                           tone === "rose" ? "border-rose-300" : "border-amber-300"}`}>
          <div className={`mx-auto h-14 w-14 rounded-full flex items-center justify-center
                           ${tone === "emerald" ? "bg-emerald-100" :
                             tone === "rose" ? "bg-rose-100" : "bg-amber-100"}`}>
            <Icon className={`h-7 w-7 ${tone === "emerald" ? "text-emerald-600" :
                              tone === "rose" ? "text-rose-600" : "text-amber-600"}`} />
          </div>
          <p className="mt-5 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center justify-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Diplomexa Billing
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[#0A192F]" data-testid="payment-result-headline">
            {isPaid ? "Payment successful" : isFailed ? "Payment failed" : "Payment processing"}
          </h1>
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
            {isPaid && "Your registration is now active. We've emailed you the invoice."}
            {isFailed && "We couldn't complete this payment. You can retry below."}
            {!isPaid && !isFailed && "We're confirming the payment with the bank. This typically takes a few seconds."}
          </p>

          {pt && (
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-sm" data-testid="payment-result-detail">
              <Detail label="Transaction" value={pt.id} mono />
              {pt.invoice_number && <Detail label="Invoice" value={pt.invoice_number} mono />}
              <Detail label="Plan" value={pt.plan_id} />
              <Detail label="Amount" value={formatMoney(pt.total_payable ?? pt.amount, pt.currency)} />
              <Detail label="Billed by" value={pt.billing_entity === "factskoot"
                ? "Factskoot Eduwellness Pvt Ltd" : "Destineze Educational Voyages FZCO"} />
              <Detail label="Gateway" value={pt.gateway || "—"} />
              <Detail label="Status" value={pt.status} />
            </div>
          )}

          <div className="mt-6 flex items-center justify-center gap-3">
            {isPaid && pt?.invoice_number && (
              <Link
                to={`/dashboard/billing/invoice/${pt.invoice_number}`}
                className="px-4 py-2 rounded-md bg-[#0F4C81] text-white text-sm font-semibold flex items-center gap-1.5"
                data-testid="payment-result-invoice-link"
              >
                <Download className="h-4 w-4" /> View invoice
              </Link>
            )}
            {isFailed && (
              <Link to="/pricing" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#0A192F] text-sm font-semibold flex items-center gap-1.5">
                Retry payment <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link to="/dashboard/billing" className="px-4 py-2 rounded-md border border-slate-200 text-sm">
              Billing center
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm ${mono ? "font-mono text-[11px]" : "font-medium"} text-[#0A192F]`}>{value}</span>
    </div>
  );
}
